import {
  IconChevronDown,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconPencil,
  IconSearch,
} from "@tabler/icons-react";
import { Link, useLocation } from "@tanstack/react-router";
import type { FC, ReactNode } from "react";
import { useMemo, useState } from "react";

import { ChatListItemRow } from "@roko/ui/components/chat/ChatListItemRow";
import type {
  TAdminNavItem,
  TChatListItem,
} from "@roko/ui/components/chat/types";
import { EnstructureLogo } from "@roko/ui/components/icons/EnstructureLogo";
import { Spinner } from "@roko/ui/components/states/Spinner";
import { Button } from "@roko/ui/components/ui/button";
import { Input } from "@roko/ui/components/ui/input";
import { ScrollArea } from "@roko/ui/components/ui/scroll-area";
import { cn } from "@roko/ui/lib/utils";

type TChatSidebarProps = {
  createNewChatLabel: string;
  searchPlaceholder: string;
  deleteChatLabel: string;
  collapseSidebarLabel?: string;
  expandSidebarLabel?: string;
  isCollapsed?: boolean;
  onToggleCollapsed?: () => void;
  chats: TChatListItem[];
  isLoadingChats?: boolean;
  loadingChatsLabel?: string;
  /** When false, hide search + recent chat history (guest / session-only). */
  showHistory?: boolean;
  adminNavItems?: TAdminNavItem[];
  onNewChat: () => void;
  onDeleteChat: (id: string) => void;
  activeChatId?: string;
  onSelectChat: (id: string) => void;
  userName?: string;
  userEmail?: string;
  userMenu?: ReactNode;
};

export const ChatSidebar: FC<TChatSidebarProps> = ({
  createNewChatLabel,
  searchPlaceholder,
  deleteChatLabel,
  collapseSidebarLabel,
  expandSidebarLabel,
  isCollapsed,
  onToggleCollapsed,
  chats,
  isLoadingChats = false,
  loadingChatsLabel,
  showHistory = true,
  adminNavItems,
  onNewChat,
  onDeleteChat,
  activeChatId,
  onSelectChat,
  userName,
  userEmail,
  userMenu,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { pathname } = useLocation();

  const filteredChats = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return chats;
    return chats.filter((chat) => chat.title.toLowerCase().includes(query));
  }, [chats, searchQuery]);

  if (isCollapsed) {
    return (
      <aside
        data-testid="chat-sidebar"
        className="flex h-full w-16 shrink-0 flex-col bg-background text-foreground"
      >
        <div className="flex h-16 items-center justify-center">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onToggleCollapsed}
            aria-label={expandSidebarLabel}
            title={expandSidebarLabel}
            className="text-foreground"
          >
            <IconLayoutSidebarLeftExpand className="size-5" />
          </Button>
        </div>

        <div className="flex flex-1 flex-col items-center gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={onNewChat}
            title={createNewChatLabel}
            className="rounded-full border-brand-orange text-brand-orange hover:bg-brand-orange/10 hover:text-brand-orange"
          >
            <IconPencil className="size-4" />
          </Button>
        </div>

        <div className="mt-auto flex flex-col items-center gap-1 pb-3">
          {adminNavItems && adminNavItems.length > 0 && (
            <nav className="flex flex-col items-center gap-1">
              {adminNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = pathname === item.to;

                return (
                  <Link
                    key={item.key}
                    to={item.to}
                    title={item.label}
                    aria-label={item.label}
                    className={cn(
                      "flex size-10 items-center justify-center rounded-lg text-foreground/80 transition-colors hover:bg-muted",
                      isActive && "bg-muted text-foreground",
                    )}
                  >
                    <Icon className="size-5" stroke={1.5} />
                  </Link>
                );
              })}
            </nav>
          )}

          {(userName || userMenu) && (
            <div className="pt-1">
              {userMenu ?? (
                <div
                  title={userName}
                  aria-label={userName}
                  className="flex size-10 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground"
                >
                  {(userName ?? "A").slice(0, 1).toUpperCase()}
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
    );
  }

  return (
    <aside
      data-testid="chat-sidebar"
      className="flex h-full w-[17.5rem] shrink-0 flex-col bg-background text-foreground"
    >
      <div className="flex h-16 items-center justify-between px-4">
        <EnstructureLogo />
        {typeof onToggleCollapsed === "function" && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onToggleCollapsed}
            aria-label={collapseSidebarLabel}
            title={collapseSidebarLabel}
            className="size-8 text-muted-foreground"
          >
            <IconLayoutSidebarLeftCollapse className="size-4" />
          </Button>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col px-4">
        <div className="space-y-3 pb-4">
          <Button
            variant="outline"
            className="h-10 w-full justify-start gap-2 rounded-full border-brand-orange bg-card text-sm font-medium text-brand-orange shadow-none hover:bg-brand-orange/10 hover:text-brand-orange"
            onClick={onNewChat}
          >
            <IconPencil className="size-4" />
            {createNewChatLabel}
          </Button>
          {showHistory ? (
            <div className="relative">
              <IconSearch className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={searchPlaceholder}
                className="h-10 rounded-full border-0 bg-muted pl-9 shadow-none"
              />
            </div>
          ) : null}
        </div>

        {showHistory ? <div className="mb-3 border-t border-border/60" role="separator" /> : null}

        {showHistory ? (
          <ScrollArea className="min-h-0 flex-1 overflow-x-hidden pb-4">
            {isLoadingChats ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                <Spinner size="small" className="text-muted-foreground" />
                {loadingChatsLabel ? <span>{loadingChatsLabel}</span> : null}
              </div>
            ) : (
              <ul className="space-y-0.5 pr-1">
                {filteredChats.map((chat) => (
                  <li key={chat.id}>
                    <ChatListItemRow
                      chat={chat}
                      isActive={activeChatId === chat.id}
                      deleteChatLabel={deleteChatLabel}
                      onSelect={() => onSelectChat(chat.id)}
                      onDelete={() => onDeleteChat(chat.id)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
        ) : (
          <div className="min-h-0 flex-1" />
        )}
      </div>

      {adminNavItems && adminNavItems.length > 0 && (
        <nav className="border-t border-border/60 px-4 pt-3 pb-3">
          <ul className="space-y-1">
            {adminNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = pathname === item.to;

              return (
                <li key={item.key}>
                  <Link
                    to={item.to}
                    className={cn(
                      "flex h-10 items-center gap-3 rounded-lg px-2 text-sm text-foreground/80 transition-colors hover:bg-muted",
                      isActive && "bg-muted font-medium text-foreground",
                    )}
                  >
                    <Icon className="size-[1.15rem] shrink-0" stroke={1.5} />
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      )}

      {(userName || userMenu) && (
        <div className="mt-auto border-t border-border/60 px-3 py-3">
          {userMenu ?? (
            <div className="flex items-center gap-2.5 rounded-lg px-1 py-1.5">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                {(userName ?? "A").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1 text-left">
                <p className="truncate text-sm font-medium text-foreground">
                  {userName}
                </p>
                {userEmail && (
                  <p className="truncate text-xs text-muted-foreground">
                    {userEmail}
                  </p>
                )}
              </div>
              <IconChevronDown className="size-4 shrink-0 text-muted-foreground" />
            </div>
          )}
        </div>
      )}
    </aside>
  );
};
